import { useState } from "react";
import { MessageSquareText, Check, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

/* "Describe it in your own words" — keyword suggestions are shown as editable
   chips and only applied when the patient explicitly confirms them. */
export function CommentBox({ qid, value, onChange, questions, onConfirmSuggestion }) {
  const [open, setOpen] = useState(!!value);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState([]);

  const analyse = async () => {
    if (!value || value.trim().length < 3) return;
    setBusy(true);
    try { setResult((await api.post("/ankle-finder/suggest", { text: value })).data); }
    catch { setResult(null); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mt-4 text-xs inline-flex items-center gap-1.5 text-[#f47a20] hover:underline" data-testid={`comment-open-${qid}`}>
        <MessageSquareText size={13} /> Describe it in your own words (optional)
      </button>
    );
  }
  const key = (s) => `${s.question_id}:${s.option_id}`;
  const visible = (result?.suggestions || []).filter((s) => !dismissed.includes(key(s)));
  return (
    <div className="mt-4" data-testid={`comment-box-${qid}`}>
      <textarea
        className="fl-input w-full min-h-[72px] text-sm" value={value || ""} maxLength={1000}
        placeholder="e.g. It aches first thing in the morning and eases once I've walked a bit"
        onChange={(e) => onChange(e.target.value)} onBlur={analyse}
        data-testid={`comment-input-${qid}`}
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-[11px] text-zinc-500">Your words are saved separately from your answers.</span>
        <button type="button" onClick={analyse} className="text-[11px] text-[#f47a20] hover:underline inline-flex items-center gap-1" data-testid={`comment-analyse-${qid}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : null} Suggest answers
        </button>
      </div>
      {result && (
        <div className="mt-3 space-y-2 text-xs" data-testid={`comment-suggestions-${qid}`}>
          {visible.map((s) => {
            const q = questions.find((x) => x.id === s.question_id);
            return (
              <div key={key(s)} className="flex items-start gap-2 p-2 rounded-md border border-[#f47a20]/40 bg-[#f47a20]/5">
                <div className="flex-1">
                  <div className="text-zinc-400">Sounds like — <span className="text-zinc-200">{q?.label}</span></div>
                  <div className="text-zinc-100 mt-0.5">"{s.option_label}"</div>
                </div>
                <button type="button" className="fl-btn text-[11px] py-1 px-2" onClick={() => { onConfirmSuggestion(s); setDismissed((d) => [...d, key(s)]); }} data-testid={`suggestion-confirm-${s.question_id}-${s.option_id}`}>
                  <Check size={12} /> Confirm
                </button>
                <button type="button" className="fl-btn-ghost text-[11px] py-1 px-2" onClick={() => setDismissed((d) => [...d, key(s)])} aria-label="Dismiss suggestion" data-testid={`suggestion-dismiss-${s.question_id}-${s.option_id}`}>
                  <X size={12} />
                </button>
              </div>
            );
          })}
          {result.negated?.length > 0 && (
            <div className="text-zinc-500" data-testid="suggestion-negated">
              You mentioned <em>not</em> having: {result.negated.map((n) => n.option_label.toLowerCase()).join("; ")} — we have not selected these.
            </div>
          )}
          {result.past_tense?.length > 0 && (
            <div className="text-amber-300/80" data-testid="suggestion-past">
              Some of this sounds like a past symptom ({result.past_tense.map((n) => n.matched).join(", ")}). If it is happening now, please choose the matching answer yourself.
            </div>
          )}
          {result.conflicts?.map((c) => (
            <div key={c.question_id} className="text-amber-300/80" data-testid="suggestion-conflict">{c.note}</div>
          ))}
          {result.unrecognised && <div className="text-zinc-500" data-testid="suggestion-unrecognised">We couldn't match your description to an answer — please choose the closest option, or pick "Not sure".</div>}
          {!visible.length && !result.negated?.length && !result.past_tense?.length && !result.unrecognised && <div className="text-zinc-500">No new suggestions.</div>}
          <div className="text-[10px] text-zinc-600">Suggestions never change the safety questions and never replace your own answers.</div>
        </div>
      )}
    </div>
  );
}
